import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Star, Heart, Camera, Film } from "lucide-react";
import PageShell from "@/components/PageShell";
import Marquee from "@/components/Marquee";
import FilmModal from "@/components/FilmModal";
import GuestbookSection from "@/components/GuestbookSection";
import Seo from "@/components/Seo";
import { WVELink } from "@/components/WVELink";
import { BRAND, HERO_IMAGES, STATS, VENUES, AWARDS, PACKAGES, REVIEWS, FILMS, HOW_IT_WORKS } from "@/data/site";

export default function Home() {
  const [activeFilm, setActiveFilm] = useState(null);
  return (
    <PageShell testId="home-page">
      <Seo
        title="Best Day Ever Productions | Fun Wedding Photography & Videography in Essex"
        description="Award-winning Essex wedding photo & video for couples who want zero cringe and all the feels. 1000+ weddings, 397 five-star reviews, packages from £1,500 with Klarna. By the Wedding Video Essex team."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Best Day Ever Productions",
          "url": "https://bestdayeverproductions.co.uk/",
          "publisher": { "@type": "Organization", "name": "Best Day Ever Productions" }
        }}
      />
      <FilmModal film={activeFilm} onClose={() => setActiveFilm(null)} />
      {/* HERO */}
      <section className="container-wide pt-10 md:pt-16 pb-12 relative" data-testid="hero">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow text-bde-pink">{BRAND.parent} · Est. {BRAND.established} · Essex</p>
            <h1 className="mt-6 font-display font-black text-[3.25rem] sm:text-7xl lg:text-[8.5rem] leading-[0.82] tracking-[-0.02em]">
              The wedding film
              <br />
              you'll <span className="italic text-bde-pink">actually</span>
              <br />
              rewatch.
            </h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl font-light text-bde-ink/75 leading-relaxed">
              We're the team behind <WVELink>{BRAND.parent}</WVELink>. 1000+ couples,
              397 five-star reviews, zero cringe. <em className="font-display italic">Best Day Ever</em> is for couples who want a film that feels like the day actually felt.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">
                Check My Date <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/work" data-testid="hero-cta-secondary" className="btn-ghost">
                See The Films
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-bde-pink text-bde-pink" />)}
                </div>
                <span className="font-mono uppercase tracking-[0.18em] text-xs">5.0 from 397 reviews</span>
              </div>
              <span className="font-mono uppercase tracking-[0.18em] text-xs text-bde-ink/60">
                Klarna · No travel fees in Essex
              </span>
            </div>
          </div>

          {/* Polaroid stack */}
          <div className="lg:col-span-5 relative h-[480px] md:h-[560px] hidden md:block">
            {HERO_IMAGES.slice(0, 4).map((img, i) => {
              const positions = [
                { top: "0%", left: "10%", rotate: "-6deg", z: 4 },
                { top: "5%", right: "0%", rotate: "5deg", z: 3 },
                { bottom: "5%", left: "0%", rotate: "8deg", z: 2 },
                { bottom: "10%", right: "8%", rotate: "-4deg", z: 1 },
              ];
              const p = positions[i];
              return (
                <div
                  key={img.src}
                  style={{
                    position: "absolute",
                    top: p.top, left: p.left, right: p.right, bottom: p.bottom,
                    zIndex: p.z,
                    transform: `rotate(${p.rotate})`,
                  }}
                  className="polaroid w-56 md:w-64"
                >
                  <span className="tape" />
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="h-56 w-full object-cover md:h-64"
                    loading={i === 0 ? "eager" : "lazy"}
                    fetchpriority={i === 0 ? "high" : "auto"}
                    decoding="async"
                    width="256"
                    height="256"
                  />
                  <p className="mt-2 font-scribble text-bde-ink text-lg leading-none">{img.caption} ♥</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y-2 border-bde-ink bg-bde-cream" data-testid="trust-strip">
        <div className="container-wide py-8 grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
          {STATS.map((s) => (
            <div key={s.label} className="flex items-baseline gap-3">
              <span className="font-display font-black text-4xl md:text-5xl text-bde-pink leading-none">{s.value}</span>
              <span className="font-mono uppercase tracking-[0.18em] text-[10px] md:text-xs text-bde-ink/70">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee items={VENUES.slice(0, 18)} variant="pink" />

      {/* INTRO COPY */}
      <section className="container-wide py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow text-bde-pink">Why we exist</p>
            <h2 className="mt-4 font-display font-black text-5xl md:text-7xl leading-[0.9]">
              Weddings,
              <br />
              <span className="italic">not the boring</span>
              <br />
              <span className="underline-pink">kind.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-lg font-light leading-relaxed text-bde-ink/80 max-w-2xl">
            <p>
              The biggest regret of couples who don't book a videographer? <em className="font-display italic text-bde-ink">Not booking a videographer.</em>
            </p>
            <p>
              <span className="font-display italic text-bde-ink">Best Day Ever Productions</span> is the same award-winning team as <WVELink>Wedding Video Essex</WVELink>, just with a freer hand. We shoot story-led films and photo galleries that don't make you cringe at the slow-mo, don't drag for 90 minutes, and don't make your guests look like they're at a tax seminar.
            </p>
            <p>
              Two cinematographers and a photo lead capturing your day, backed by a wider team with 17+ years of wedding experience and 1000+ weddings between them. Canon Cinema Cameras. A drone. One person from your first DM to the final film. No call centres, no being passed around.
            </p>
            <div className="flex flex-wrap gap-3 pt-4">
              <span className="sticker-pink"><Camera className="h-3 w-3" /> 500+ Photos</span>
              <span className="sticker-pink"><Film className="h-3 w-3" /> Cinematic Films</span>
              <span className="sticker-pink"><Heart className="h-3 w-3" /> One Team, No Fluff</span>
            </div>
          </div>
        </div>
      </section>

      {/* WORK PREVIEW */}
      <section className="bg-bde-ink text-bde-cream py-24 md:py-32" data-testid="work-preview">
        <div className="container-wide">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="eyebrow text-bde-pink">Real weddings, real tears</p>
              <h2 className="mt-4 font-display font-black text-5xl md:text-7xl leading-[0.9]">
                Films
                <br />
                <span className="italic text-bde-pink">for re-watching.</span>
              </h2>
            </div>
            <Link to="/work" className="btn-outline-pink" data-testid="view-all-work">
              See all the work →
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FILMS.slice(0, 6).map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setActiveFilm(f)}
                className="group relative overflow-hidden border-2 border-bde-cream/20 hover:border-bde-pink transition-colors text-left"
                data-testid={`film-${f.id}`}
              >
                <img
                  src={`https://img.youtube.com/vi/${f.id}/hqdefault.jpg`}
                  alt={f.title}
                  className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                  width="480"
                  height="360"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-bde-ink to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 flex items-end justify-between">
                  <div>
                    <h3 className="font-display text-2xl text-bde-cream leading-none">{f.title}</h3>
                    {f.venue && <p className="mt-1 text-xs text-bde-cream/70 font-mono uppercase tracking-[0.18em]">{f.venue}</p>}
                  </div>
                  <span className="inline-flex h-12 w-12 items-center justify-center bg-bde-pink text-bde-ink font-mono text-xs group-hover:scale-110 transition-transform">▶</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container-wide py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="eyebrow text-bde-pink">Process</p>
            <h2 className="mt-4 font-display font-black text-5xl md:text-7xl leading-[0.9]">
              Booking
              <br />
              <span className="italic">in three</span>
              <br />
              <span className="underline-pink">steps.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.step} className="bg-white border-2 border-bde-ink p-6 ink-shadow">
                <span className="font-display font-black text-6xl text-bde-pink leading-none">{s.step}</span>
                <h3 className="mt-4 font-display text-2xl">{s.title}</h3>
                <p className="mt-2 text-sm text-bde-ink/70 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGES TEASER */}
      <section className="bg-bde-pinkSoft border-y-2 border-bde-ink py-24 md:py-32">
        <div className="container-wide">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="eyebrow text-bde-pink">Investment</p>
              <h2 className="mt-4 font-display font-black text-5xl md:text-7xl leading-[0.9]">
                Simple pricing.
                <br />
                <span className="italic">No surprises.</span>
              </h2>
            </div>
            <Link to="/packages" className="btn-primary" data-testid="view-all-packages">
              All packages →
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {PACKAGES.map((p) => (
              <div
                key={p.id}
                className={`relative bg-white border-2 border-bde-ink p-6 flex flex-col ${p.popular ? "pink-shadow" : "ink-shadow"}`}
                data-testid={`package-card-${p.id}`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-4 sticker-pink">★ Most Popular</span>
                )}
                <p className="eyebrow text-bde-pink">{p.badge}</p>
                <h3 className="mt-3 font-display text-2xl leading-tight">{p.name}</h3>
                <p className="mt-4 font-display font-black text-4xl">From {p.price}</p>
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-bde-ink/60 mt-1">{p.monthly}</p>
                <p className="mt-4 text-sm text-bde-ink/70 italic">{p.tagline}</p>
                <Link to="/packages" className="mt-6 btn-outline-pink !py-3 self-start">More info →</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO GUESTBOOK TEASER */}
      <GuestbookSection variant="teaser" />

      {/* REVIEWS TEASER */}
      <section className="container-wide py-24 md:py-32">
        <p className="eyebrow text-bde-pink">Receipts</p>
        <h2 className="mt-4 font-display font-black text-5xl md:text-7xl leading-[0.9] max-w-4xl">
          What couples
          <br />
          <span className="italic">actually said.</span>
        </h2>

        <div className="mt-12 grid lg:grid-cols-3 gap-5">
          {REVIEWS.slice(0, 3).map((r, i) => (
            <blockquote
              key={i}
              className="bg-white border-2 border-bde-ink p-8 ink-shadow flex flex-col"
              data-testid={`review-card-${i}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-bde-pink text-bde-pink" />)}
              </div>
              <p className="font-display text-xl md:text-2xl leading-snug font-medium">"{r.quote}"</p>
              <footer className="mt-6 pt-4 border-t border-bde-ink/10">
                <p className="font-mono uppercase tracking-[0.18em] text-xs">{r.name}</p>
                <p className="text-xs text-bde-ink/50 mt-1">via {r.source} · {r.date}</p>
              </footer>
            </blockquote>
          ))}
        </div>

        <div className="mt-10">
          <Link to="/reviews" className="btn-ghost" data-testid="view-all-reviews">All 397 reviews →</Link>
        </div>
      </section>

      {/* AWARDS */}
      <section className="bg-bde-ink text-bde-cream py-16">
        <div className="container-wide">
          <p className="eyebrow text-bde-pink text-center">As featured / awarded</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-10 md:gap-16">
            {AWARDS.map((a) => (
              <img key={a.name} src={a.url} alt={a.name} className="h-20 md:h-24 object-contain opacity-90 hover:opacity-100 transition-opacity" loading="lazy" decoding="async" />
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="container-wide py-24 md:py-32 text-center">
        <p className="eyebrow text-bde-pink">The first step</p>
        <h2 className="mt-4 font-display font-black text-6xl md:text-8xl leading-[0.85]">
          Is your date
          <br />
          <span className="italic text-bde-pink">still free?</span>
        </h2>
        <p className="mx-auto mt-8 max-w-xl text-lg font-light text-bde-ink/70">
          We only take a limited number of weddings each weekend. Pop your date in and we'll come back within 24 hours.
        </p>
        <Link to="/contact" className="mt-10 btn-primary" data-testid="final-cta">
          Check My Date →
        </Link>
      </section>
    </PageShell>
  );
}
